import React, { useEffect, useState } from "react";
import {
  endOfMonth,
  isBefore,
  startOfMonth,
  subDays,
  subMonths,
} from "date-fns";
import DatePicker, { registerLocale } from "react-datepicker";
import pt from "date-fns/locale/pt";

import "react-datepicker/dist/react-datepicker.css";

import { Card } from "../../components/card";
import { Skeleton } from "../../components/skeleton";
import { api } from "../../services/api";
import { currencyFormat } from "../../utils/currencyFormat";
import { dateFormat } from "../../utils/dateFormat";
import { serverDateRange } from "../../utils/server-date-range";

import {
  Container,
  Content,
  CardsWrapper,
  Table,
  TableWrapper,
  ActionStatus,
  PaginationWrapper,
  ArrowPageRightIcon,
  ArrowPageLeftIcon,
} from "./styles";
import { Link } from "react-router-dom";
import { Button } from "../../components/button";

interface IItems {
  type: string
  ourNumber: string
  documentNumber: string
  amount: number
  dueAt: string;
  barCode: string
  digitableLine: string
  customerName: string
  customerDocument: string
  customerZipCode: string
  customerState: string
  url: string
  id: string
  date: string
  paidAt: string
  internalProcess?: string | Date
}

interface PropsStatus {
  title: string;
  label: "payed" | "pending" | "delayed";
}

registerLocale("pt", pt);

const periodShortcuts = [
  {
    label: "Este mês",
    getRange: () => {
      const now = new Date();
      return { start: startOfMonth(now), end: endOfMonth(now) };
    },
  },
  {
    label: "Mês passado",
    getRange: () => {
      const previous = subMonths(new Date(), 1);
      return { start: startOfMonth(previous), end: endOfMonth(previous) };
    },
  },
  {
    label: "Últimos 30 dias",
    getRange: () => {
      const now = new Date();
      return { start: subDays(now, 29), end: now };
    },
  },
];

export const Charge = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<IItems[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [startDate, setStartDate] = useState(() => startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(() => endOfMonth(new Date()));

  useEffect(() => {
    setLoading(true);
    const range = serverDateRange(startDate, endDate);

    api.get(`/billets?page=${currentPage}&start=${range.start}&end=${range.end}`).then(({ data }) => {
      setTotalPages(data.total_pages + 1);
      setCurrentPage(data.current_page);
      setItems(data.items);
      setLoading(false);
    });
  }, [currentPage, startDate, endDate]);

  function handleStartDate(date: Date) {
    setStartDate(date);
    setCurrentPage(0);
  }

  function handleEndDate(date: Date) {
    setEndDate(date);
    setCurrentPage(0);
  }

  function handleShortcut(start: Date, end: Date) {
    setStartDate(start);
    setEndDate(end);
    setCurrentPage(0);
  }

  function handlePrevious() {
    if (currentPage + 1 > 1) {
      setCurrentPage(currentPage - 1);
    }
  }

  function handleNext() {
    if (currentPage + 1 < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  }

  function getStatus(billet: IItems): PropsStatus {

    if (!billet.paidAt) {
      // Cancelado: 'CANCELLED' ou uma data de cancelamento (ex.: '2026-10-01')
      const internalProcess = billet?.internalProcess;
      if (
        internalProcess === 'CANCELLED' ||
        internalProcess instanceof Date ||
        (typeof internalProcess === 'string' && /^\d{4}-\d{2}-\d{2}/.test(internalProcess))
      ) {
        return {
          title: 'Cancelado',
          label: 'delayed',
        }
      }

      // Pagamento identificado, aguardando crédito (ex.: WAITING_TRANSFER_TO_MASTER)
      if (billet?.internalProcess && billet.internalProcess !== 'DONE') {
        return {
          title: 'Processando',
          label: 'pending',
        }
      }

      const now = new Date();
      if (isBefore(new Date(billet.dueAt), now)) {
        return {
          title: 'Atrasado',
          label: 'delayed',
        }
      }
    }

    if (billet.paidAt) {
      return {
        title: 'Pago',
        label: 'payed',
      }
    }

    return {
      title: "Em Aberto",
      label: "pending"
    }
  }

  return (
    <Container>
      <Content>
        <div className="w-full flex justify-end">
          <Link to="/deposit/invoice" className="w-full max-w-[240px] mr-8 mt-12">
            <Button title="Nova cobrança" />
          </Link>
        </div>
        <CardsWrapper>
          <Card title="Pagos" color="#49D294" value="R$ 0,00" />
          <Card title="Atrasados" color="#E74C3C" value="R$ 0,00" />
          <Card title="Pendentes" color="#F6BC4D" value="R$ 0,00" />
        </CardsWrapper>
        <Table>
          <div className="flex justify-between items-end mb-5 flex-wrap gap-4">
            <div>
              <h3 className="text-xl">Período</h3>
              <span>Selecione um período para filtrar:</span>
              <div className="flex mt-2">
                <DatePicker
                  className="bg-[#142249] rounded px-2 py-1 text-center text-white flex items-center"
                  selected={startDate}
                  onChange={handleStartDate}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  locale="pt"
                  dateFormat="dd/MM/yyyy"
                />
                <span className="px-2 py-1">até</span>
                <DatePicker
                  className="bg-[#142249] rounded px-2 py-1 text-center text-white flex items-center"
                  selected={endDate}
                  onChange={handleEndDate}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  locale="pt"
                  dateFormat="dd/MM/yyyy"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {periodShortcuts.map((shortcut) => (
                <button
                  key={shortcut.label}
                  type="button"
                  onClick={() => {
                    const { start, end } = shortcut.getRange();
                    handleShortcut(start, end);
                  }}
                  className="rounded border border-[#142249] px-3 py-1 text-sm text-[#142249] hover:bg-[#142249] hover:text-white"
                >
                  {shortcut.label}
                </button>
              ))}
            </div>
          </div>
          <TableWrapper>
            {!loading && items.length <= 0 && (
              <div className="p-6">
                <div className="text-center">
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum lançamento para o período selecionado</h3>
                  <p className="mt-1 text-sm text-gray-500">Selecione outro período para a busca</p>

                </div>
              </div>
            )}
            <Skeleton space={4} isActive={loading} repeat={5} />
            <table>
              {!loading && items.length > 0 && (
                <thead>
                  <tr>
                    <th>Vencimento</th>
                    <th>Pagador</th>
                    <th>Status</th>
                    <th>Valor</th>
                  </tr>
                </thead>
              )}

              <tbody>
                {!loading && items.map((item) => {
                  return (
                    <tr key={item.id}>
                      <td>{dateFormat(item.dueAt)}</td>
                      <td>{item.customerName}</td>
                      <td>
                        <ActionStatus status={getStatus(item).label}>
                          <span>{getStatus(item).title}</span>
                        </ActionStatus>
                      </td>
                      <td>{currencyFormat(item.amount)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableWrapper>
        </Table>
        <PaginationWrapper>
          <ArrowPageLeftIcon onClick={handlePrevious} />
          <span>Página {currentPage + 1} de {totalPages}</span>
          <ArrowPageRightIcon onClick={handleNext} />
        </PaginationWrapper>
      </Content>
    </Container>
  );
};
